import React from "react";
import { useTranslation } from "react-i18next";
import { useSAPDataStore } from "../store/SAPDataStore";
import { dateRangeStyles, cx } from "../styles";

interface DateRangeFieldProps {
  /** Override the open handler; defaults to opening the global picker modal. */
  onOpenPicker?: () => void;
  className?: string;
}

const DateRangeField: React.FC<DateRangeFieldProps> = ({
  onOpenPicker,
  className = "",
}) => {
  const { t } = useTranslation();
  const { dateRange, setDatePickerOpen } = useSAPDataStore();

  const openPicker = () => {
    if (onOpenPicker) onOpenPicker();
    else setDatePickerOpen(true);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openPicker();
  };

  const handleTouch = (e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openPicker();
  };

  return (
    <div
      className="relative cursor-pointer"
      data-tour="date-range"
      // z-10, not z-50: the sticky top bar is z-50, and at equal z-index the
      // later element in the DOM wins — so this field painted over the header
      // once the page scrolled under it. 10 still lifts it above plain siblings.
      style={{ minWidth: "max-content", zIndex: 10 }}
      onClick={handleClick}
      onMouseDown={handleClick}
      onTouchStart={handleTouch}
    >
      <div
        className={cx(
          dateRangeStyles.button.base,
          dateRange.start && dateRange.end
            ? dateRangeStyles.button.withRange
            : dateRangeStyles.button.withoutRange,
          className,
        )}
        aria-label="Select date range"
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          position: "relative",
          minWidth: "max-content",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <svg
          className={dateRangeStyles.icon}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="18"
            rx="4"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          />
          <line
            x1="16"
            y1="2"
            x2="16"
            y2="6"
            stroke="currentColor"
            strokeWidth="2"
          />
          <line
            x1="8"
            y1="2"
            x2="8"
            y2="6"
            stroke="currentColor"
            strokeWidth="2"
          />
          <line
            x1="3"
            y1="10"
            x2="21"
            y2="10"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
        <span className="flex items-center gap-1 flex-grow pointer-events-none ml-3">
          <span
            className={
              dateRange.start && dateRange.end
                ? dateRangeStyles.text.range
                : dateRangeStyles.text.placeholder
            }
          >
            {dateRange.start && dateRange.end
              ? t("dateRangeFromTo", {
                  start: dateRange.start,
                  end: dateRange.end,
                })
              : t("noDateRangeSelected") || "No date range selected"}
          </span>
          {!dateRange.start || !dateRange.end ? (
            <span className="text-orange-200 text-sm ml-1">*</span>
          ) : null}
        </span>
      </div>
    </div>
  );
};

export default DateRangeField;
